import React from 'react'
import VidCommands from './VidCommands'
import UrlPost from './UrlPost'
import parseDomain from 'parse-domain'
import styles from './url-answer.module.scss'
import cn from 'classnames'
import WithSelection from '../shared/WithSelection'
import WithTooltip from 'components/shared/WithTooltip'
import { extractUrls, LinkPreview, twitterRegexp } from 'components/shared/LinkPreview'
import { getKnovLinkRegexp } from 'components/shared/LinkPreview'
import {
    getBsvTransactionState,
    getBsvTransactionTooltip,
    BSV_TX_STATES,
} from 'utils/bsvTransactionStatus'
import { bsvTxExplorerUrl } from 'utils/bsvExplorerUrls'
import { truncateText } from 'lib/textUtils'
import { getDisplayUrlSelection } from 'lib/urlAnswerSelection'
import { resolveAnswerGeometryProjection } from 'lib/answerGeometryProjection'
import { mediaBucketsEnabled } from 'lib/mediaBuckets'
import { isYouTubeTimeCommand } from 'lib/youtubeTimeCommand'
import { getRenderableTweetMediaItems } from 'lib/tweetCardHeight'

const getTweetIdFromUrlModel = url => {
    const explicitId = url?.tweet_id || url?.service_json?.id
    if (explicitId) return String(explicitId)
    if (url?.service_name === 'twitter' && url?.service_id) return String(url.service_id)
    return null
}

const getTwitterPreviewAddress = answer => {
    const url = answer?.url
    if (url?.address) return url.address

    const deltaTwitterLink = extractUrls(answer?.delta_json)?.nonKnovLinks?.find(href =>
        twitterRegexp.test(href),
    )
    if (deltaTwitterLink) return deltaTwitterLink

    const tweetId = getTweetIdFromUrlModel(url)
    return tweetId ? `https://x.com/i/status/${tweetId}` : ''
}

const getUrlModelWithPreviewAddress = (url, previewAddress) => {
    if (!url || url.address || !previewAddress) return url
    return { ...url, address: previewAddress }
}

export default class UrlAnswer extends React.PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            linkPreviewDescription: null,
        }
    }

    componentDidUpdate(prevProps) {
        const previousAddress = prevProps.answer?.url?.address
        const currentAddress = this.props.answer?.url?.address
        if (previousAddress !== currentAddress && this.state.linkPreviewDescription) {
            this.setState({ linkPreviewDescription: null })
        }
    }

    isUserSignedIn() {
        return window.$('#knovigator-body').attr('data-user') === 'true'
    }

    showVotes = () => {
        return typeof this.props.showVotes === 'undefined' ? true : this.props.showVotes
    }

    squash = ev => {
        ev.stopPropagation()
        ev.preventDefault()
    }

    voteContainerParent = React.createRef()

    showVidCommands = () => this.props.isYoutube && isYouTubeTimeCommand(this.props.command)

    isTwitterUrl = url => {
        return twitterRegexp.test(url?.address || '') || !!getTweetIdFromUrlModel(url)
    }

    handleLinkPreviewMetadataLoaded = metadata => {
        const description = metadata?.description || null
        if (description === this.state.linkPreviewDescription) return
        this.setState({ linkPreviewDescription: description })
    }

    renderBsvAttachmentStatus = matchingBsvAttachment => {
        const bsvTx = matchingBsvAttachment?.bsv_tx
        const txId = bsvTx?.tx_id
        const state = getBsvTransactionState(bsvTx, txId)
        const tooltip = getBsvTransactionTooltip(bsvTx, txId, state)

        switch (state) {
            case BSV_TX_STATES.PENDING:
                return (
                    <WithTooltip tip={tooltip}>
                        <i className="fa fa-spinner fa-spin" />
                    </WithTooltip>
                )

            case BSV_TX_STATES.AWAITING_RESUBMISSION:
                return (
                    <WithTooltip tip={tooltip}>
                        <i className="fa fa-clock-o" />
                    </WithTooltip>
                )

            case BSV_TX_STATES.CONFIRMED:
            default:
                return (
                    <WithTooltip tip={tooltip}>
                        <a
                            href={bsvTxExplorerUrl(txId)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={ev => ev.stopPropagation()}
                        >
                            <i className="fa fa-cube" />
                        </a>
                    </WithTooltip>
                )
        }
    }

    render() {
        const answer = this.props.answer
        const url = answer.url
        const address = getTwitterPreviewAddress(answer) || answer.url?.address || ''
        const urlModel = getUrlModelWithPreviewAddress(url, address)
        const parse = parseDomain(address)
        let domain
        if (parse) {
            domain = `${parse.domain}.${parse.tld}`
        }
        const editStyles = this.props.editMode ? styles.edit : null
        const isEmbedStyles = this.props.isEmbed ? styles.isEmbed : null
        const embedUrlContainerStyles = this.props.isEmbed ? styles.embedUrlContainer : null
        const commandContainerTitleStyles = this.props.isTitle ? styles.commandContainerTitle : null
        const fallbackPreviewDescription = url?.service_json?.description
        const previewDescription = this.state.linkPreviewDescription || fallbackPreviewDescription
        const projectedSelection =
            resolveAnswerGeometryProjection(answer)?.url?.visibleSelectionText
        const selectionText = mediaBucketsEnabled()
            ? projectedSelection || ''
            : getDisplayUrlSelection({
                  answer,
                  address,
                  previewDescription,
                  isYoutube: this.props.isYoutube,
              })

        // Find matching BsvAttachment for this URL's full text
        const matchingBsvAttachment = answer?.bsv_attachments?.find(
            att => att.attachment_id === url?.full_text_attachment_id,
        )
        const twitterMediaMenuOwnsStatus =
            this.isTwitterUrl(url) && getRenderableTweetMediaItems(url).length > 0

        return (
            <div className={styles.urlAnswerComp}>
                {this.showVidCommands() && (
                    <div className={cn(styles.commandContainer, commandContainerTitleStyles)}>
                        <VidCommands
                            command={this.props.command}
                            vidStart={this.props.vidStart}
                            vidLoop={this.props.vidLoop}
                            comment={answer}
                            getYtPlayer={this.props.getYtPlayer}
                            vidLoaded={this.props.vidLoaded}
                            loadVid={this.props.loadVid}
                        />
                    </div>
                )}

                <div className={cn(styles.flexContainer)}>
                    <div className={cn(styles.userPostContent, editStyles, isEmbedStyles)}>
                        {this.props.answer.url && address && !getKnovLinkRegexp().test(address) && (
                            <div
                                className={cn(styles.urlContainer, embedUrlContainerStyles)}
                                data-answer={this.props.answer.id}
                            >
                                <div className={styles.urlWithLogo}>
                                    <div className={styles.sourceContainer}>
                                        <div className={cn(styles.source)}>
                                            <div
                                                className={cn(styles.sourceHeader, 'source-header')}
                                            >
                                                {!this.props.isYoutube && (
                                                    <div className={styles.linkPreviewWrapper}>
                                                        <LinkPreview
                                                            url={address}
                                                            answerId={this.props.answer.id}
                                                            domain={domain}
                                                            inViewCallback={
                                                                this.props.inViewCallback
                                                            }
                                                            urlModel={urlModel}
                                                            estimatedWidth={
                                                                this.props.estimatedWidth
                                                            }
                                                            onPreviewClick={
                                                                this.props.onPreviewClick
                                                            }
                                                            onHeadEnter={this.props.onHeadEnter}
                                                            onHeadLeave={this.props.onHeadLeave}
                                                            onMouseEnterAnswer={
                                                                this.props.onMouseEnter
                                                            }
                                                            onMouseLeaveAnswer={
                                                                this.props.onMouseLeave
                                                            }
                                                            onMetadataLoaded={
                                                                this.handleLinkPreviewMetadataLoaded
                                                            }
                                                            matchingBsvAttachment={
                                                                matchingBsvAttachment
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {selectionText && !this.isTwitterUrl(url) && (
                                            <div
                                                className={cn(
                                                    styles.selectionContainer,
                                                    'web-quote-outer',
                                                )}
                                                data-answer={this.props.answer.id}
                                            >
                                                <WithSelection>
                                                    <div
                                                        className={cn(
                                                            styles.selection,
                                                            'web-quote-inner',
                                                        )}
                                                        data-answer-id={this.props.answer.id}
                                                    >
                                                        {truncateText(selectionText, 280)}
                                                    </div>
                                                </WithSelection>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {matchingBsvAttachment &&
                                    !this.props.isYoutube &&
                                    !twitterMediaMenuOwnsStatus && (
                                        <div className={styles.bsvStatusOverlay}>
                                            {this.renderBsvAttachmentStatus(matchingBsvAttachment)}
                                        </div>
                                    )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }
}
